import { capitalize } from "@material-ui/core";
import { FormStore, GlobalStore } from "@roc/feature-app-core";
import { action, computed, makeObservable, observable, toJS } from "mobx";
import { CompToolService } from "@roc/feature-comp-tool";
import { isNumber } from "@roc/feature-utils";
import { formatCurrency, formatNumberWithCommas } from "@roc/ui/utils";

const form = {
  fields: {
    PRESET: {
      values: {
        value: '',
        error: null,
        rule: '',
      }
    }, TYPE: {
      values: {
        value: '',
        error: null,
        rule: '',
      }
    },
    ROOMS: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      }
    },
    BEDROOMS: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      }
    },
    FULL_BATHS: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      }
    },
    HALF_BATHS: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      }
    },
    PRICE: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    LEASE_PRICE: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      }
    },
    AREA: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    YEARBUILT: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    LOTSIZE: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      }
    },
    GEO__DIST: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    DOM: {
      min: {
        value: null,
        error: null,
        rule: '',
      },
      max: {
        value: null,
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    SQFTPRICE: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    ASSESSOR_TAX: {
      min: {
        value: '',
        error: null,
        rule: '',
      },
      max: {
        value: '',
        error: null,
        rule: '',
      },
      suggestedRange: {
        min: null,
        max: null
      }
    },
    ACTIVE_LISTINGS: {
      values: {
        value: [],
        error: null,
        rule: '',
      }
    },
    SALETYPE: {
      values: {
        value: [],
        error: null,
        rule: '',
      }
    },
    IS_ACTIVE: {
      values: {
        value: [],
        error: null,
        rule: '',
      }
    },
    SOLD_LISTINGS: {
      values: {
        value: [],
        error: null,
        rule: '',
      }
    },
    WAS_FLIPPED: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    UNDER_CONTRACT: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    DISTRESSED: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    APPRAISALS: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    HAS_PHOTOS: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    MONTHS: {
      values: {
        value: '',
        error: null,
        rule: '',
      }
    },
    CONDITION_TAG: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    CONDITION_CONFIDENCE_TAG: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
    SCHOOL_DISTRICT: {
      values: {
        value: null,
        error: null,
        rule: '',
      }
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const arrayValues = ['WAS_FLIPPED', 'CONDITION_TAG',
  'HAS_PHOTOS', 'CONDITION_CONFIDENCE_TAG', 'SCHOOL_DISTRICT'
  , "UNDER_CONTRACT", "DISTRESSED", "APPRAISALS", "ACTIVE_LISTINGS", "SOLD_LISTINGS", "IS_ACTIVE", "SALETYPE", "TYPE"
];

const rangeValues = [
  'GEO__DIST', 'ROOMS', 'BEDROOMS', 'FULL_BATHS', 'HALF_BATHS', "PRICE", "SQFTPRICE", "ASSESSOR_TAX", "AREA", "YEARBUILT", "DOM"
];

const simpleValues = ['MONTHS', 'PRESET'];

const booleanValues = [
  "APPRAISALS", "ACTIVE_LISTINGS", "SOLD_LISTINGS", "UNDER_CONTRACT", "DISTRESSED", "WAS_FLIPPED", "HAS_PHOTOS", "IS_ACTIVE", "SALETYPE"
];

const includeMissingFalseValues = ["ROOMS", "BEDROOMS", "FULL_BATHS", "HALF_BATHS"];

const fieldsWithSuggestedRanges = [
  'AREA', 'YEARBUILT', 'PRICE', 'SQFTPRICE',
  'ASSESSOR_TAX', 'DOM', 'GEO__DIST'
];

interface FilterValue {
  id: string;
  label: string;
}

export class CompToolFiltersStore extends FormStore {
  globalStore: GlobalStore;
  compToolService: CompToolService;
  propertyTypeOptions: any[];
  presetOptions: any[];
  schoolDistricts: any[];
  activeFilters: FilterValue[];
  drawingPolygons: any[];
  filterPolygons: any[];

  constructor(globalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.compToolService = new CompToolService();

    makeObservable(this, {
      setDefaultFilters: action,
      loadFilterOptions: action,
      loadPresetOptions: action,
      onFieldsChange: action,
      propertyTypeOptions: observable,
      presetOptions: observable,
      clearFilters: action,
      getMultiFormValues: action,
      getFiltersRequest: action,
      getFormValue: action,
      getMinMaxValue: action,
      schoolDistricts: observable,
      activeFilters: observable,
      setActiveFilters: action,
      drawingPolygons: observable,
      filterPolygons: observable,
      setDrawingPolygons: action,
      setFiltersPolygons: action,
      activePreset: computed,
      setSuggestedRanges: action
    });

    this.reset();
  }

  reset() {
    super.reset();
    this.activeFilters = [];
    this.schoolDistricts = [
      {
        label: 'Yes',
        value: [''],
      },
      {
        label: 'No',
        value: '',
      }
    ];
    this.drawingPolygons = [];
    this.filterPolygons = [];
  }

  getMultiFormValues(): any {

    const data = {};

    for (const key in this.form.fields) {
      const field = this.form.fields[key];

      if (arrayValues.includes(key)) {
        const value = field.values.value;
        if (isNumber(value) || (Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value)) {
          data[key] = {
            include_missing: key !== 'SALETYPE',
            values: value
          };
        }
      }

      if (key === 'IS_ACTIVE') {
        const isActiveValues = field.values?.value;
        if (isActiveValues?.length === 1) {
          data[key] = {
            include_missing: true,
            values: field.values.value
          };
        }
      }

      if (simpleValues.includes(key) && field.values.value) {
        data[key] = {
          include_missing: true,
          values: [field.values.value]
        };
      }


      if (field.min && field.min.value) {
        data[key] = {
          ...data[key],
          min: field.min.value
        };
      }

      if (field.max && field.max.value) {
        data[key] = {
          ...data[key],
          max: field.max.value
        };
      }

      if (includeMissingFalseValues.includes(key) && field.min?.value) {
        data[key] = {
          include_missing: false,
          ...data[key],
        };
      }
    }
    return data;
  };

  onFieldsChange(key, field, value) {
    if (typeof value === 'string' && !isNaN(value as any) && value.trim() !== '') {
      value = value.includes('.') ? parseFloat(value) : parseInt(value, 10);
    }

    this.form.fields[key][field].value = value;
  }

  loadFilterOptions(data) {

    if (data.TYPE) {
      this.propertyTypeOptions = data.TYPE.values.map(type => {
        return {
          label: capitalize(type),
          value: type
        }
      })

      if (this.form.fields.TYPE.values.value) {

        const typeValues = Array.isArray(this.form.fields.TYPE.values.value)
          ? this.form.fields.TYPE.values.value
          : [this.form.fields.TYPE.values.value];

        const validValues = this.propertyTypeOptions.map(option => option.value);
        const allValid = typeValues.every(val => validValues.includes(val));

        if (!allValid) {
          this.form.fields.TYPE.values.value = '';
          this.clearFilter('TYPE');
        }
      }

      if (data.SCHOOL_DISTRICT && data.SCHOOL_DISTRICT.values.length > 0) {
        this.schoolDistricts.forEach(item => {
          if (item.label === 'Yes') {
            item.value = data.SCHOOL_DISTRICT.values;
          }
        });
      }
    }
  }

  loadPresetOptions(preset_options) {
    if (preset_options) {
      this.presetOptions = Object.entries(preset_options).map(([label, value]) => ({
        label,
        value,
      }));
    }
  }

  setDefaultFilters(data) {
    this.onFieldsChange('IS_ACTIVE', 'values', []);
  }

  setSuggestedRanges(data) {
    if (!data) return;
    Object.keys(data).map(key => {
      try {
        if (fieldsWithSuggestedRanges.includes(key) && data[key] && this.form.fields[key]) {
          const field = this.form.fields[key];
          field.suggestedRange.min = this.formatSuggestedRange(data[key].min, key);
          field.suggestedRange.max = this.formatSuggestedRange(data[key].max, key);
        }
      } catch (err) {
        console.error(err);
        console.error(`There was an error setting the default range of ${key}`);
      }
    });
  }

  private formatSuggestedRange(value: number, key: string) {
    if (value === 0) return '0';
    if (!value) return '';
    if (key === 'YEARBUILT') {
      return `${value}`;
    }
    return formatNumberWithCommas(value);
  }

  getFiltersRequest() {
    const {
      TYPE,
      ROOMS,
      BEDROOMS,
      FULL_BATHS,
      HALF_BATHS,
      PRICE,
      LEASE_PRICE,
      AREA,
      YEARBUILT,
      LOTSIZE,
      GEO__DIST,
      DOM,
      SQFTPRICE,
      ASSESSOR_TAX,
      SOLD_LISTINGS,
      WAS_FLIPPED,
      HAS_PHOTOS,
      CONDITION_TAG,
      CONDITION_CONFIDENCE_TAG,
      SCHOOL_DISTRICT,
      UNDER_CONTRACT,
      DISTRESSED,
      APPRAISALS,
      IS_ACTIVE,
      SALETYPE
    } = this.getMultiFormValues();

    const filtersFields =
    {
      fields: {
        TYPE,
        ROOMS,
        BEDROOMS,
        FULL_BATHS,
        HALF_BATHS,
        PRICE,
        LEASE_PRICE,
        AREA,
        YEARBUILT,
        LOTSIZE,
        SQFTPRICE,
        ASSESSOR_TAX,
        SOLD_LISTINGS,
        WAS_FLIPPED,
        HAS_PHOTOS,
        CONDITION_TAG,
        CONDITION_CONFIDENCE_TAG,
        SCHOOL_DISTRICT,
        UNDER_CONTRACT,
        DISTRESSED,
        APPRAISALS,
        GEO__DIST,
        DOM,
        IS_ACTIVE,
        SALETYPE
      }
    }

    return filtersFields;
  }

  getPresetFilter() {
    const { PRESET } = this.getMultiFormValues();
    if (!PRESET?.values || PRESET?.values.length === 0) {
      return {};
    }
    return {
      preset: PRESET?.values[0]
    }
  }

  getFormValue(key) {
    const field = this.form.fields[key];
    return field.values.value;
  }

  getMinMaxValue(key, subKey) {
    const field = this.form.fields[key];
    return field[subKey];
  }

  clearFilters() {
    if (this.activeFilters?.length > 0) {
      this.activeFilters.forEach(filter => {
        this.clearFilter(filter.id);
      })
    }
    this.activeFilters = [];
  }

  setActiveFilters() {
    const {
      TYPE,
      GEO__DIST,
      DOM,
      APPRAISALS,
      SOLD_LISTINGS,
      UNDER_CONTRACT,
      DISTRESSED,
      WAS_FLIPPED,
      MONTHS,
      ROOMS,
      BEDROOMS,
      FULL_BATHS,
      HALF_BATHS,
      HAS_PHOTOS,
      SCHOOL_DISTRICT,
      PRICE,
      SQFTPRICE,
      ASSESSOR_TAX,
      AREA,
      YEARBUILT,
      ACTIVE_LISTINGS,
      PRESET,
      SALETYPE
    } = this.getMultiFormValues();
    const filters = [];

    //If we have a preset we don't show the other chips
    if (PRESET?.values && PRESET?.values.length > 0) {
      const presetFilterValue = PRESET?.values[0];
      filters.push({ id: "PRESET", label: `Preset: ${this.getPresetOptionLabel(presetFilterValue)}` });
      this.activeFilters = filters;
      return;
    }

    if (SALETYPE?.values) {
      filters.push({ id: "SALETYPE", label: `Appraisals Required` });
    }

    if (this.form.fields.IS_ACTIVE.values.value?.length === 0) {
      filters.push({ id: "IS_ACTIVE", label: `Sold Listings Required` });
    }

    if (TYPE?.values) {
      const typeFilterValue = TYPE?.values.join(', ')
      filters.push({ id: "TYPE", label: `Property Type: ${typeFilterValue}` });
    }

    this.addActiveFilterRangeValue(GEO__DIST, "GEO__DIST", "Distance from SP", filters);

    this.addActiveFilterRangeValue(DOM, "DOM", "Days on Market", filters);

    if (APPRAISALS?.values?.length === 1) {
      filters.push({ id: "APPRAISALS", label: `Appraisals Required` });
    }
    if (ACTIVE_LISTINGS?.values?.length === 1) {
      filters.push({ id: "ACTIVE_LISTINGS", label: `Active Listings Required` });
    }
    if (SOLD_LISTINGS?.values?.length === 1) {
      filters.push({ id: "SOLD_LISTINGS", label: `Sold Listings Required` });
    }
    if (UNDER_CONTRACT?.values?.length === 1) {
      filters.push({ id: "UNDER_CONTRACT", label: `Under Contract Required` });
    }
    if (DISTRESSED?.values?.length === 1) {
      filters.push({ id: "DISTRESSED", label: `Distressed Required` });
    }
    if (WAS_FLIPPED?.values?.length === 1) {
      filters.push({ id: "WAS_FLIPPED", label: `Flips Required` });
    }
    if (MONTHS?.values) {
      filters.push({ id: "MONTHS", label: `Recency in the last: ${MONTHS.values[0]} months` });
    }
    if (ROOMS?.min || ROOMS?.max) {
      filters.push({ id: "ROOMS", label: 'Rooms: ' + (ROOMS?.min === ROOMS?.max ? ROOMS?.max : `${ROOMS.min} - ${ROOMS.max}`) });
    }
    if (BEDROOMS?.min || BEDROOMS?.max) {
      filters.push({ id: "BEDROOMS", label: 'Bedrooms: ' + (BEDROOMS?.min === BEDROOMS?.max ? BEDROOMS?.max : `${BEDROOMS.min} - ${BEDROOMS.max}`) });
    }
    if (FULL_BATHS?.min || FULL_BATHS?.max) {
      filters.push({ id: "FULL_BATHS", label: 'Full Baths: ' + (FULL_BATHS?.min === FULL_BATHS?.max ? FULL_BATHS?.max : `${FULL_BATHS.min} - ${FULL_BATHS.max}`) });
    }
    if (HALF_BATHS?.min || HALF_BATHS?.max) {
      filters.push({ id: "HALF_BATHS", label: 'Half Baths: ' + (HALF_BATHS?.min === HALF_BATHS?.max ? HALF_BATHS?.max : `${HALF_BATHS.min} - ${HALF_BATHS.max}`) });
    }
    if (HAS_PHOTOS?.values?.length === 1) {
      filters.push({ id: "HAS_PHOTOS", label: `Photos Required` });
    }
    if (SCHOOL_DISTRICT?.values?.length > 0) {
      filters.push({ id: "SCHOOL_DISTRICT", label: `School District Required` });
    }

    this.addActiveFilterRangeValue(PRICE, "PRICE", "Price", filters, (value) => formatCurrency(value, 0));

    this.addActiveFilterRangeValue(SQFTPRICE, "SQFTPRICE", "Price/Sq.Ft.", filters, (value) => formatCurrency(value, 0));

    this.addActiveFilterRangeValue(ASSESSOR_TAX, "ASSESSOR_TAX", "Taxes/Year", filters, (value) => formatCurrency(value, 0));

    this.addActiveFilterRangeValue(AREA, "AREA", "Square Feet", filters);

    this.addActiveFilterRangeValue(YEARBUILT, "YEARBUILT", "Year Built", filters);

    if (this.filterPolygons?.length > 0) {
      filters.push({ id: "POLYGONS", label: `Selected Area` });
    }

    this.activeFilters = filters;
  }

  clearFilter(filterName, applyValidation = false) {
    if (applyValidation && !this.canRemoveFilter(filterName)) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'At least one of the following filters must remain active: Active Listings or Sold Listings',
      });
      return false;
    }

    if (arrayValues.includes(filterName)) {
      if (filterName === "IS_ACTIVE") {
        this.form.fields[filterName].values.value = ["True"];
      } else {
        this.form.fields[filterName].values.value = [];
      }
      return true;
    }
    if (rangeValues.includes(filterName)) {
      this.form.fields[filterName].min.value = null;
      this.form.fields[filterName].max.value = null;
      return true;
    }
    if (simpleValues.includes(filterName)) {
      this.form.fields[filterName].values.value = '';
      return true;
    }
    if (booleanValues.includes(filterName)) {
      this.form.fields[filterName].values.value = ["True", "False"];
      return true;
    }
    if (filterName === 'POLYGONS') {
      this.filterPolygons = [];
      this.drawingPolygons = [];
    }
    return true;
  }

  canRemoveFilter(filterName) {
    if (filterName === "IS_ACTIVE") {
      const activeListingsActive = this.activeFilters.find(filter => filter.id === "ACTIVE_LISTINGS");
      if (!activeListingsActive) {
        return false;
      }
    }
    if (filterName === "ACTIVE_LISTINGS") {
      const soldListingsActive = this.activeFilters.find(filter => filter.id === "IS_ACTIVE");
      if (!soldListingsActive) {
        return false;
      }
    }
    return true;
  }

  addActiveFilterRangeValue(value, key: string, label: string, filters: FilterValue[], formatFunction = (value) => value) {
    if (value?.min || value?.max) {
      if (!value?.min) {
        filters.push({ id: key, label: `${label} < ${formatFunction(value.max || 0)}` });
      } else if (!value?.max) {
        filters.push({ id: key, label: `${label} > ${formatFunction(value.min || 0)}` });
      } else {
        filters.push({ id: key, label: `${label}: ${formatFunction(value.min || 0)} - ${formatFunction(value.max || 0)}` });
      }
    }
  }

  setDrawingPolygons(drawingPolygons: any[]) {
    this.drawingPolygons = drawingPolygons;
  }
  setFiltersPolygons(filterPolygons: any[]) {
    this.filterPolygons = filterPolygons;
  }
  addDrawingPolygon(polygon: any[]) {
    this.drawingPolygons = [...this.drawingPolygons, polygon];
  }

  hasActiveFilters() {
    return this.activeFilters.length > 0;
  }

  getPolygonsFilter() {
    if (this.filterPolygons.length > 0) {
      return {
        polygon: toJS(this.filterPolygons[0].map(coord => ([coord[1], coord[0]])))
      }
    }
    return {};
  }

  getPresetOptionLabel(presetValue: string) {
    if (!this.presetOptions || this.presetOptions.length === 0) {
      return presetValue;
    }
    const presetOption = this.presetOptions.find(option => option.value === presetValue);
    return presetOption ? presetOption.label : presetValue;
  }

  get activePreset() {
    return this.form?.fields["PRESET"]?.values?.value;
  }
}